import { T } from "@/lib/i18n";
import { useEffect, useState, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Cell } from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, MousePointerClick, Target, TrendingUp, Sparkles, Zap, RefreshCw, MessageCircle, ExternalLink, Clock, Instagram, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCampaign, getCampaignLeads } from "@/lib/api";
import { TID } from "@/constants/testIds";

const CHANNEL_COLORS = {
  "Google Ads": "#E0262C",
  Meta: "#09090b",
  WhatsApp: "#10b981",
  LinkedIn: "#f59e0b",
};

const fmt = (n) => new Intl.NumberFormat("en").format(Math.round(n || 0));

const _fmtTime = (secs) => {
  if (secs <= 0) return "0s";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};

export const CampaignDashboard = ({ campaignId, onReset }) => {
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const leadsRef = useRef([]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const d = await getCampaign(campaignId);
        if (!alive) return;
        setData(d);
        // Load leads only when active
        if (d.status === "active" || d.status === "completed") {
          try {
            const l = await getCampaignLeads(campaignId, 4);
            if (!alive) return;
            const merged = [
              ...l.leads.map((x) => ({ ...x, _id: Math.random().toString(36).slice(2) })),
              ...leadsRef.current,
            ].slice(0, 12);
            leadsRef.current = merged;
            setLeads(merged);
          } catch {}
        }
      } catch {}
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [campaignId]);

  if (!data) {
    return (
      <div className="border border-[#e4e4e7] p-16 text-center">
        <div className="inline-flex items-center gap-2 text-sm font-mono text-[#71717a]">
          <Sparkles className="w-4 h-4 blink" /> Caricamento campagna...
        </div>
      </div>
    );
  }

  const { metrics, status } = data;
  const waNumber = (data.targeting?.whatsapp_number || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
  const waMsg = data.targeting?.whatsapp_message || "";
  const waUrl = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(waMsg)}` : "";
  const igUrl = data.targeting?.instagram_url;

  // Warm-up state
  if (status === "warming_up" || status === "scheduled") {
    return (
      <section data-testid={TID.campaignDash} className="space-y-6">
        <WarmupBanner data={data} onReset={onReset} waUrl={waUrl} igUrl={igUrl} />
      </section>
    );
  }

  return (
    <section data-testid={TID.campaignDash} className="space-y-6">
      {/* Status bar */}
      <div className="flex items-center justify-between border border-[#e4e4e7] px-6 py-4 bg-[#09090b] text-white">
        <div className="flex items-center gap-3">
          <span className="relative flex h-2.5 w-2.5">
            {status === "active" && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10b981] opacity-75" />}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${status === "active" ? "bg-[#10b981]" : "bg-[#71717a]"}`} />
          </span>
          <span className="font-mono text-xs uppercase tracking-widest">
            {status === "active" ? T("Campagna attiva") : status === "completed" ? T("Campagna completata") : status}
          </span>
          <span className="text-[10px] text-[#a1a1aa] font-mono">· ID {campaignId.slice(0, 8)}</span>
        </div>
        <Button data-testid={TID.newCampaignBtn} onClick={onReset} variant="outline" size="sm"
          className="bg-transparent border-white/20 text-white hover:bg-white hover:text-black rounded-sm h-8">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Nuova campagna
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#e4e4e7] divide-x divide-[#e4e4e7]">
        <KPI icon={Eye} label="Impressioni" value={fmt(metrics.impressions)} />
        <KPI icon={MousePointerClick} label="Click" value={fmt(metrics.clicks)} sub={`${metrics.ctr}% CTR`} />
        <KPI icon={Target} label="Conversioni" value={fmt(metrics.conversions)} sub={`€ ${metrics.cpc} CPC`} accent />
        <KPI icon={TrendingUp} label="Speso" value={`€ ${fmt(metrics.spent)}`} sub={`di € ${fmt(metrics.budget)}`} progress={metrics.progress} />
      </div>

      {status === "completed" && data.report_sent && (
        <div className="border border-[#10b981] bg-[#f0fdf4] p-5 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-[#10b981]" strokeWidth={2.5} />
          <div>
            <div className="font-display text-base">{T("Report inviato all'admin")}</div>
            <div className="text-xs text-[#52525b] font-mono">CSV + XLSX + metriche spediti a info@graficawebdesign.com</div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 border border-[#e4e4e7] p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-xl">{T("Performance nel tempo")}</h3>
            <span className="text-[10px] uppercase font-mono text-[#71717a] tracking-widest">LIVE</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={metrics.timeline}>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="t" stroke="#a1a1aa" fontSize={11} axisLine={false} tickLine={false} />
              <YAxis stroke="#a1a1aa" fontSize={11} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#09090b", border: "none", borderRadius: 4, color: "white", fontSize: 12 }} />
              <Line type="monotone" dataKey="impressions" stroke="#E0262C" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="clicks" stroke="#09090b" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="conversions" stroke="#10b981" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-4 text-xs font-mono">
            <LegendDot color="#E0262C" label="Impressioni" />
            <LegendDot color="#09090b" label="Click" />
            <LegendDot color="#10b981" label="Conversioni" />
          </div>
        </div>

        <div className="lg:col-span-2 border border-[#e4e4e7] p-6">
          <h3 className="font-display text-xl mb-6">{T("Canali")}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={metrics.channels} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" hide />
              <YAxis dataKey="channel" type="category" stroke="#09090b" fontSize={12} axisLine={false} tickLine={false} width={80} />
              <Tooltip contentStyle={{ background: "#09090b", border: "none", borderRadius: 4, color: "white", fontSize: 12 }} />
              <Bar dataKey="clicks" radius={[0, 2, 2, 0]}>
                {metrics.channels.map((c, i) => (
                  <Cell key={i} fill={CHANNEL_COLORS[c.channel] || "#E0262C"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-4">
            {metrics.channels.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs font-mono">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2" style={{ background: CHANNEL_COLORS[c.channel] }} />
                  <span>{c.channel}</span>
                </div>
                <span className="text-[#71717a]">€{c.spent} · {c.ctr}% CTR</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Budget optimizer */}
      <div className="border border-[#e4e4e7] p-6 bg-gradient-to-r from-[#f4f4f5] to-white flex items-start gap-4">
        <div className="w-10 h-10 aurora-gradient flex items-center justify-center flex-shrink-0">
          <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-display text-lg">{T("Ottimizzazione budget AI")}</h4>
            <span className="text-[10px] font-mono uppercase text-[#10b981]">{T("● Attiva")}</span>
          </div>
          <p className="text-sm text-[#52525b]">{T("L'AI redistribuisce automaticamente il budget verso i canali più performanti.")}</p>
        </div>
      </div>

      {/* WhatsApp CTA preview */}
      {waUrl && (
        <div className="border border-[#e4e4e7] bg-[#f0fdf4] p-6 flex flex-col lg:flex-row items-start lg:items-center gap-4">
          <div className="w-10 h-10 bg-[#25D366] flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-display text-lg">{T("WhatsApp CTA attivo")}</h4>
              <span className="text-[10px] font-mono uppercase text-[#166534]">● +{waNumber}</span>
            </div>
            <p className="text-sm text-[#52525b]">{T("I lead cliccano e si apre questa chat sul loro telefono.")}</p>
            <code className="block text-[11px] font-mono text-[#166534] mt-2 truncate">{waUrl}</code>
          </div>
          <a href={waUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-5 py-2.5 text-sm font-medium transition-colors">
            <ExternalLink className="w-4 h-4" /> Apri chat demo
          </a>
        </div>
      )}

      {/* Live leads */}
      <div className="border border-[#e4e4e7]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e4e4e7]">
          <h3 className="font-display text-xl">{T("Lead in tempo reale")}</h3>
          <span className="text-[10px] uppercase font-mono text-[#71717a] tracking-widest">
            <span className="w-1.5 h-1.5 inline-block rounded-full bg-[#ef4444] blink mr-2" />
            STREAMING
          </span>
        </div>
        <div data-testid={TID.liveLeads} className="divide-y divide-[#f4f4f5] max-h-[480px] overflow-y-auto">
          <AnimatePresence initial={false}>
            {leads.map((l) => {
              const isWa = l.channel === "WhatsApp" && waUrl;
              const Wrapper = isWa ? "a" : "div";
              const wrapperProps = isWa ? { href: waUrl, target: "_blank", rel: "noreferrer" } : {};
              return (
                <motion.div key={l._id} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                  <Wrapper {...wrapperProps} className={`flex items-center gap-4 px-6 py-3 hover:bg-[#fafafa] ${isWa ? "cursor-pointer" : ""}`}>
                    <img src={l.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{l.name}</span>
                        <span className="text-xs text-[#71717a] font-mono">{l.age}y · {l.city}</span>
                      </div>
                      <div className="text-xs text-[#71717a] mt-0.5">{l.interest}</div>
                    </div>
                    <div className="text-right hidden sm:block">
                      <div className="text-[10px] font-mono uppercase" style={{ color: CHANNEL_COLORS[l.channel] }}>{l.channel}</div>
                      <div className="text-[10px] font-mono text-[#a1a1aa]">match {l.match_score}%</div>
                    </div>
                    <span className="text-[10px] font-mono uppercase px-2 py-1 rounded-sm" style={{
                      background: l.action === "conversion" ? "#10b981" : l.action === "click" ? "#E0262C" : "#f4f4f5",
                      color: l.action === "impression" ? "#71717a" : "#ffffff",
                    }}>{l.action}</span>
                  </Wrapper>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const KPI = ({ icon: Icon, label, value, sub, accent, progress }) => (
  <div className={`p-6 ${accent ? "bg-[#09090b] text-white" : "bg-white"} relative overflow-hidden`}>
    {accent && <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-30 blur-3xl aurora-gradient-radial" />}
    <div className={`relative flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono mb-4 ${accent ? "text-white/70" : "text-[#71717a]"}`}>
      <Icon className="w-3.5 h-3.5" /> {T(label)}
    </div>
    <div className={`relative font-display text-4xl leading-none ${accent ? "aurora-text" : ""}`}>{value}</div>
    {sub && <div className={`relative mt-2 text-xs font-mono ${accent ? "text-white/70" : "text-[#71717a]"}`}>{sub}</div>}
    {progress !== undefined && (
      <div className="relative mt-3 h-1 bg-black/10">
        <div className="h-1 aurora-gradient" style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
    )}
  </div>
);

const LegendDot = ({ color, label }) => (
  <div className="flex items-center gap-2 text-[#71717a]">
    <span className="w-2 h-2" style={{ background: color }} /> {T(label)}
  </div>
);

const WarmupBanner = ({ data, onReset, waUrl, igUrl }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const warmupEnd = data.warmup_ends_at ? new Date(data.warmup_ends_at).getTime() : null;
  const remain = warmupEnd ? Math.max(0, Math.floor((warmupEnd - now) / 1000)) : 0;

  return (
    <div data-testid={TID.warmupBanner} className="border border-[#e4e4e7] bg-[#09090b] text-white p-10 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-40 blur-3xl aurora-gradient-radial" />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 aurora-gradient flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#a1a1aa]">{T("● Warm-up in corso")}</div>
              <h2 className="font-display text-3xl mt-1">{T("L'AI si allinea con le aste")}</h2>
            </div>
          </div>
          <Button onClick={onReset} variant="outline" size="sm"
            className="bg-transparent border-white/20 text-white hover:bg-white hover:text-black rounded-sm">
            {T("Nuova campagna")}
          </Button>
        </div>

        <p className="text-[#a1a1aa] max-w-2xl mb-8">
          Durante il warm-up (2-3 giorni reali) la nostra AI analizza le aste Meta e Google per trovare il miglior slot per i tuoi lead.
          A differenza di Google Ads, Aurora-ADV parte già ottimizzata — <span className="aurora-text font-semibold">{T("risultati accurati fin dal primo click")}</span>.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10">
          <WarmStat label="Tempo rimanente" value={_fmtTime(remain)} />
          <WarmStat label="Durata campagna" value={`${data.duration_days} ${T("giorni")}`} />
          <WarmStat label="Budget totale" value={`€ ${data.total_budget.toFixed(0)}`} />
          <WarmStat label="Status" value={data.status === "warming_up" ? "Warming up" : "Schedulata"} aurora />
        </div>

        <div className="mt-8 space-y-3">
          <div className="flex items-start gap-3 text-sm">
            <CheckCircle2 className="w-4 h-4 text-[#10b981] mt-0.5 flex-shrink-0" />
            <span className="text-[#d4d4d8]">{T("Pagamento confermato · ordine aperto per lavorazione")}</span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <CheckCircle2 className="w-4 h-4 text-[#10b981] mt-0.5 flex-shrink-0" />
            <span className="text-[#d4d4d8]">{T("[Ordine pagato] email inviata al team amministrativo")}</span>
          </div>
          {igUrl && (
            <div className="flex items-start gap-3 text-sm">
              <Instagram className="w-4 h-4 text-[#E64FA7] mt-0.5 flex-shrink-0" />
              <span className="text-[#d4d4d8]">{T("Follower reali Instagram inizieranno dal 2°-3° giorno su")} <code className="font-mono text-xs">{igUrl}</code></span>
            </div>
          )}
          {waUrl && (
            <div className="flex items-start gap-3 text-sm">
              <MessageCircle className="w-4 h-4 text-[#25D366] mt-0.5 flex-shrink-0" />
              <span className="text-[#d4d4d8]">{T("Pulsante WhatsApp attivo per i lead profilati")}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const WarmStat = ({ label, value, aurora }) => (
  <div className="p-5 border-r border-white/10 last:border-r-0 bg-black/30">
    <div className="text-[10px] uppercase tracking-widest text-[#71717a] font-mono mb-2">{T(label)}</div>
    <div className={`font-display text-2xl ${aurora ? "aurora-text" : ""}`}>{value}</div>
  </div>
);
